import React, { Component } from "react";
import { Redirect, Link } from "react-router-dom";
import { Breadcrumbs, BreadcrumbsItem } from "@sainsburys-tech/fable";
import { forkJoin, of } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import { componentFromStream } from "recompose";
import withLoadingAndErrorHandling from "../../utils/reactive/wrapPageApiCall.js";
import StoreZoneConfigureForm from "../StoreZoneConfigureForm";
import PageTitle from "../PageTitle";
import apiProxy from "../../api/apiProxy";
import { ToastConsumer } from "../WithToastManager";
import * as zoneTypeKeys from "../../model/lighting-zone-types/zoneTypeKeys.js";
import tryParseInt from "../../utils/tryParseInt";
import { Event } from "../Tracking";

class StoreZoneConfigurePage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      redirectToStoreZoneList: false,
      zoneOptions: props.zoneOptions,
      zones: props.zones,
      initialValues: this.getInitialValues(props),
      existingZoneNames: props.existingZoneNames,
      creatingZone: false,
      errorCreatingZone: false
    };
  }

  getInitialValues = props => {
    if (!props.storeConfiguration) return {};
    return props.storeConfiguration.zones.reduce(
      (initialValues, r) => {
        const propZoneId = props.zoneId;
        const reduceZoneId = r.zone.zoneId;
        if (propZoneId === reduceZoneId) {
          initialValues = {
            selectedZone: props.zoneId,
            otherZoneName: "",
            hasLuxSensor:
              r.hasLuxMeter !== undefined ? r.hasLuxMeter.toString() : null,
            luxSensorId:
              r.luxMeterCbusGroupId !== undefined
                ? r.luxMeterCbusGroupId.toString()
                : null,
            pirSensor:
              r.pirSensorIds !== undefined
                ? r.pirSensorIds.map((z, i) => ({
                    name: `pirSensor[${i}]`,
                    value: z
                  }))
                : [{ name: "pirSensor0", value: "" }],
            cbusGroupId:
              r.lightsCbusGroupId !== undefined
                ? r.lightsCbusGroupId.toString()
                : null,
            pinId: r.pinId !== undefined ? r.pinId.toString() : null,
            version: r.version,
            zoneType: r.zone.zoneType
          };
        }
        return initialValues;
      },
      {
        selectedZone: "",
        otherZoneName: "",
        hasLuxSensor: "",
        luxSensorId: "",
        cbusGroupId: "",
        pirSensor: [{ name: "pir0", value: "" }]
      }
    );
  };

  handleSubmit = values => {
    const data = {
      version: values.version !== undefined ? values.version : null
    };

    if (values.zoneType === zoneTypeKeys.IO) {
      data.pinId = tryParseInt(values.pinId);
    } else {
      const hasLuxSensor = values.hasLuxSensor === "true";
      data.hasLuxMeter = hasLuxSensor;
      data.lightsCbusGroupId = tryParseInt(values.cbusGroupId);

      if (hasLuxSensor) {
        data.luxMeterCbusGroupId = tryParseInt(values.luxSensorId);
      }

      if (values.pirSensor.length > 0) {
        data.pirSensorIds = values.pirSensor
          .map(s => tryParseInt(s.value))
          .filter(s => s !== null);
      }
    }

    return apiProxy.storeLighting.setStoreZoneConfiguration(
      this.props.storeId,
      values.selectedZone,
      data
    );
  };

  handleSaveComplete = values => {
    const selectedZone = this.state.zoneOptions.find(
      z => z.value === values.selectedZone
    ).label;

    this.props.toastManager.add(
      "success",
      "Zone configured",
      `${selectedZone} has been configured successfully.`
    );

    Event("Store Zone Configuration Event", "Changes Saved", "(not set)");
    this.redirectToStoreZoneList();
  };

  redirectToStoreZoneList = () => {
    this.setState({ redirectToStoreZoneList: true });
  };

  handleCreateZone = (zoneName, values) => {
    this.setState({ creatingZone: true });
    return apiProxy.zone.createZone(zoneName).then(
      zone => {
        const existingZones = this.props.storeConfiguration
          ? this.props.storeConfiguration.zones
          : [];

        const zones = JSON.parse(JSON.stringify(this.state.zones));
        zones.push(zone);

        const mappedZoneOptions = mapZoneOptions(existingZones, zones);

        this.setState(existingState => ({
          zoneOptions: mappedZoneOptions,
          zones: zones,
          initialValues: {
            ...values,
            selectedZone: zone.zoneId,
            zoneType: zone.zoneType,
            otherZoneName: ""
          },
          existingZoneNames: [...existingState.existingZoneNames, zone.name],
          creatingZone: false,
          errorCreatingZone: false
        }));
      },
      () => {
        this.setState({ creatingZone: false, errorCreatingZone: true });
      }
    );
  };

  render() {
    if (this.state.redirectToStoreZoneList) {
      return (
        <Redirect
          to={`/lighting/stores/${this.props.storeId}/commissioning/zones`}
          push={true}
        />
      );
    }

    return (
      <PageTitle title="Configure zone">
        <fieldset>
          <Breadcrumbs shouldShowCurrentPage={true}>
            <BreadcrumbsItem href="/stores/">
              Stores
            </BreadcrumbsItem>
            <BreadcrumbsItem href={`/stores/${this.props.storeId}`}>
              {this.props.storeName}
            </BreadcrumbsItem>
            <BreadcrumbsItem href={`/lighting/stores/${this.props.storeId}/commissioning/`}>
              Commissioning
            </BreadcrumbsItem>
            <BreadcrumbsItem href={`/lighting/${window.location.href.split("lighting/")[1]}`}>
              Configure Zone
            </BreadcrumbsItem>
          </Breadcrumbs>

          <h2>Configure zone</h2>

          <StoreZoneConfigureForm
            zoneId={this.props.zoneId === undefined ? {} : this.props.zoneId}
            zones={this.state.zones}
            zoneOptions={this.state.zoneOptions}
            existingZoneNames={this.state.existingZoneNames}
            existingCbusIds={this.props.existingCbusIds}
            existingPinIds={this.props.existingPinIds}
            storeId={this.props.storeId}
            storeConfiguration={this.props.storeConfiguration}
            onSubmit={this.handleSubmit}
            onSaveComplete={this.handleSaveComplete}
            onCancel={this.redirectToStoreZoneList}
            onCreateZone={this.handleCreateZone}
            initialValues={this.state.initialValues}
            creatingZone={this.state.creatingZone}
            errorCreatingZone={this.state.errorCreatingZone}
          />
        </fieldset>
      </PageTitle>
    );
  }
}

const mapIds = (zones, zoneId, zoneType) => {
  if (zoneType === zoneTypeKeys.IO) {
    return []
      .concat(
        ...zones
          .filter(f => {
            return f.zone.zoneType === zoneType;
          })
          .map(zone => [zone.pinId])
      )
      .filter(x => x);
  } else {
    return []
      .concat(
        ...zones
          .filter(f => {
            return f.zone.zoneId !== zoneId;
          })
          .map(zone => [zone.lightsCbusGroupId, zone.luxMeterCbusGroupId])
      )
      .filter(x => x);
  }
};

const mapZoneNames = zones => {
  const zoneNames = zones.map(zone => zone.name);
  return zoneNames;
};

const mapZoneOptions = (existingStoreZones, zones, zoneId = undefined) => {
  if (zoneId !== undefined) {
    return zones
      .filter(f => f.zoneId === zoneId)
      .map(zone => ({ value: zone.zoneId, label: zone.name }));
  } else {
    const existingZones = existingStoreZones.map(zone =>
      zone.zone.name.toLowerCase()
    );

    const allZones = zones
      .filter(z => !existingZones.includes(z.name.toLowerCase()))
      .sort(
        (a, b) =>
          +b.isSystemZone - +a.isSystemZone || a.name.localeCompare(b.name)
      )
      .map(zone => ({
        value: zone.zoneId,
        label: zone.name
      }));

    allZones.push({ value: "Other", label: "Other" });
    return allZones;
  }
};

export default componentFromStream(props$ => {
  return withLoadingAndErrorHandling(() =>
    props$.pipe(
      mergeMap(
        ({ storeId, getZones, getStore, zoneId, getStoreZoneConfiguration }) =>
          forkJoin([
            getZones(),
            getStore(storeId),
            of(zoneId),
            zoneId !== undefined
              ? getStoreZoneConfiguration(storeId, zoneId)
              : of(undefined)
          ])
      ),
      map(([zones, store, zoneId]) => (
        <ToastConsumer>
          {toastManagerContext => (
            <StoreZoneConfigurePage
              toastManager={toastManagerContext}
              storeId={store.store.storeId}
              storeName={store.store.name}
              storeConfiguration={store.storeConfiguration}
              existingCbusIds={mapIds(
                store.storeConfiguration ? store.storeConfiguration.zones : [],
                zoneId,
                zoneTypeKeys.CBUS
              )}
              existingPinIds={mapIds(
                store.storeConfiguration ? store.storeConfiguration.zones : [],
                zoneId,
                zoneTypeKeys.IO
              )}
              existingZoneNames={mapZoneNames(zones.zones)}
              zoneId={zoneId}
              zoneOptions={mapZoneOptions(
                store.storeConfiguration ? store.storeConfiguration.zones : [],
                zones.zones,
                zoneId
              )}
              zones={zones.zones}
            />
          )}
        </ToastConsumer>
      ))
    )
  );
});
